"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.markIpChargeDraftsSavedSchema = exports.ipChargeDraftResponseSchema = exports.updateIpChargeDraftSchema = exports.saveIpChargeDraftsSchema = exports.ipChargeDraftInputSchema = exports.listIpChargesQuerySchema = exports.reverseIpChargeSchema = exports.bridgeIpChargeBatchSchema = exports.captureIpChargeSchema = exports.IP_OT_DOCTOR_ROLE_LABEL = exports.IP_OT_REQUIRED_DOCTOR_ROLES = exports.IP_OT_DOCTOR_ROLES = exports.ipOtDoctorSchema = exports.ipOtDoctorRoleEnum = exports.ipChargeLedgerStatusEnum = exports.ipChargeSourceTypeEnum = void 0;
exports.missingOtDoctorRoles = missingOtDoctorRoles;
const zod_1 = require("zod");
const lab_newborn_schema_1 = require("./lab-newborn.schema");
/**
 * IP charge capture contracts — ADR-085 Phase 1.
 *
 * `unitRate`, `grossAmount`, `gstAmount` and every other money field are
 * ABSENT from the input by design. The caller states WHAT was delivered; the
 * capture service resolves what it costs through the Service Master, the
 * active price version and the tax master. A rate supplied by the caller would
 * be a price the billing screen originated — the exact thing ADR-085 forbids,
 * and a price no report could later explain.
 */
exports.ipChargeSourceTypeEnum = zod_1.z.enum([
    'ADMISSION_FEE',
    'ROOM_RENT',
    'BED_CHARGE',
    'NURSING',
    'DOCTOR_VISIT',
    'PROCEDURE',
    'OT',
    'LAB',
    'RADIOLOGY',
    'PHARMACY',
    'INVENTORY_CONSUMPTION',
    'PACKAGE_ADJUSTMENT',
    'CORPORATE_ADJUSTMENT',
    'CGHS_ADJUSTMENT',
    'MISCELLANEOUS',
    'MANUAL',
    'REVERSAL',
]);
exports.ipChargeLedgerStatusEnum = zod_1.z.enum([
    'PENDING',
    'BILLED',
    'REVERSED',
    'PACKAGE_COVERED',
    'WAIVED',
]);
/**
 * THE THEATRE TEAM ON A CHARGE — desk, 2026-08-23: "should ask Surgeon Name,
 * Assist. Surgoean/Assistant Name, Anestics details… these 3 info is mandatory".
 *
 * Three ROLES, and a line may name several people in each ("this can be added
 * multiple in single line"): two surgeons scrub on one procedure and three
 * assistants is routine.
 *
 * THE ASSISTANT IS NO LONGER MANDATORY (2026-08-26, desk instruction). Plenty
 * of theatre work is done by a surgeon and an anaesthetist with nobody
 * assisting, and a rule that refuses those captures does not make an assistant
 * appear — it makes the desk type a name that is not true. The field is still
 * offered, and still recorded when there is one.
 */
exports.ipOtDoctorRoleEnum = zod_1.z.enum(['SURGEON', 'ASSISTANT_SURGEON', 'ANAESTHETIST']);
exports.ipOtDoctorSchema = zod_1.z.object({
    role: exports.ipOtDoctorRoleEnum,
    /**
     * `User.id`, when the person is one — NOT `Doctor.id`, which matches zero
     * rows against the ids billing carries and fails silently as a blank name.
     * Optional because a visiting surgeon who has no login is still the surgeon.
     */
    doctorId: zod_1.z.string().uuid().optional(),
    /** Required for exactly that reason: the NAME is the fact being recorded. */
    doctorName: zod_1.z.string().min(1).max(160),
    /** "Anesthetics details" — technique, agent, anything the theatre noted. */
    notes: zod_1.z.string().max(1000).optional(),
});
/** The three roles a theatre charge can record, in the order the desk names them. */
exports.IP_OT_DOCTOR_ROLES = ['SURGEON', 'ASSISTANT_SURGEON', 'ANAESTHETIST'];
/**
 * The roles a theatre charge cannot be captured WITHOUT.
 *
 * A subset of the three on purpose: the assistant is offered but optional, so
 * this is what `missingOtDoctorRoles` enforces while `IP_OT_DOCTOR_ROLES`
 * stays the list of what a screen shows.
 */
exports.IP_OT_REQUIRED_DOCTOR_ROLES = ['SURGEON', 'ANAESTHETIST'];
/** One label per role, so the screen and the server error read the same. */
exports.IP_OT_DOCTOR_ROLE_LABEL = {
    SURGEON: 'Surgeon',
    ASSISTANT_SURGEON: 'Assistant surgeon',
    ANAESTHETIST: 'Anaesthetist',
};
/**
 * Which roles are still missing from a set of entries.
 *
 * ONE implementation, shared by the browser (to keep the tick down) and the
 * server (to refuse the capture), so the screen can never believe a line is
 * complete that the server will reject.
 */
function missingOtDoctorRoles(entries) {
    return exports.IP_OT_REQUIRED_DOCTOR_ROLES.filter((role) => !entries.some((e) => e.role === role && (e.doctorName ?? '').trim() !== ''));
}
exports.captureIpChargeSchema = zod_1.z.object({
    /**
     * THE DRAFT THIS CAPTURE APPROVES, when it came from one.
     *
     * Approval is a MOVE: the server deletes the draft inside the same
     * transaction that writes the ledger row, so a line is never in both places.
     * Absent for every machine-raised charge (lab, pharmacy, accruals), which has
     * no draft and never did.
     */
    draftId: zod_1.z.string().uuid().optional(),
    /** `ChargeMaster` id — the ADR-083 Service Master. */
    serviceId: zod_1.z.string().uuid(),
    sourceType: exports.ipChargeSourceTypeEnum,
    /**
     * The raising module's own primary key. Together with `sourceType` this is
     * the idempotency key, so it must be STABLE across retries — a value derived
     * from `Date.now()` or a fresh uuid defeats deduplication entirely.
     */
    sourceId: zod_1.z.string().min(1).max(120),
    sourceModule: zod_1.z.string().min(1).max(60),
    /**
     * Groups this line with every other one the desk's current "Save All"
     * action is capturing sequentially — see `saveAll`'s own header in
     * `useDraftLines.ts` for why they cannot be sent as one request. Minted
     * client-side once per batch (a fresh uuid per press), never reused across
     * two separate Save Alls. Omitted for a single-line capture (the tick on
     * one row, or any machine-raised charge), which keeps bridging immediately
     * as it always has — batching is opt-in, not the new default shape.
     */
    captureBatchId: zod_1.z.string().uuid().optional(),
    quantity: zod_1.z.number().int().positive().max(10_000).default(1),
    serviceDate: zod_1.z.coerce.date().optional(),
    description: zod_1.z.string().max(1000).optional(),
    encounterId: zod_1.z.string().uuid().optional(),
    /** References `User`, not `Doctor` — join the master via `Doctor.userId`. */
    performedByDoctorId: zod_1.z.string().uuid().optional(),
    /**
     * THE COUNTER'S OWN RATE, for an OPEN-VALUE service only — integer paise.
     *
     * IP works the same ₹1-sentinel rule OP billing works: a service whose
     * catalogue price is the ₹1 sentinel is priced at the counter, and the
     * operator types what it costs. `IpChargeCaptureService.resolveOpenValueRate`
     * has enforced that rule from the start — it prices through
     * `IpPriceResolutionService` first, accepts this only when what came back IS
     * the sentinel, and REFUSES it on any catalogue-priced service — but this
     * schema had no field to carry the number, so `validateRequest` stripped it
     * and the whole path was unreachable over HTTP. The guard was never the gap;
     * the wire was.
     *
     * Validated as "an amount" here and as "above the sentinel" there
     * (`isValidOpenValueRate`), so the ₹1 placeholder left untouched is refused
     * by the one rule both desks share rather than by a second copy of it.
     */
    unitRate: zod_1.z.number().int().positive().optional(),
    /**
     * The tier THIS line is priced at — C1-C4 — chosen on the line itself.
     *
     * Desk, 2026-08-22: "c1,c2,c3,c4 can be selectable" on the line item. The
     * whole-admission `apply-tier` is unchanged and remains the way to move every
     * line at once; this is the same assertion made for one charge, resolved by
     * the same engine (`IpPriceResolutionService` has taken a `tier` all along —
     * only the wire was missing).
     *
     * OMITTED IS THE DEFAULT AND STAYS THE DEFAULT: with no tier the price comes
     * from the OPEN BED OCCUPANCY, which is the tier the patient is actually
     * lying in. An override is a deliberate act, never a silent one.
     */
    tier: zod_1.z.enum(['C1', 'C2', 'C3', 'C4']).optional(),
    /**
     * The operating team. REQUIRED — one of each of the three roles — when the
     * service's own master says so (`ChargeMaster.requiresOtDoctors`, the "OT
     * Doctor Name requirement" checkbox), and refused when it does not: a theatre
     * team on a lab test is a data-entry accident, not extra information.
     *
     * The SERVICE decides, never the caller, so a screen cannot opt out of the
     * requirement by omitting the field.
     */
    otDoctors: zod_1.z.array(exports.ipOtDoctorSchema).max(20).optional(),
    /** Integer paise, applied after price resolution. Never a substitute for it. */
    discountAmount: zod_1.z.number().int().nonnegative().optional(),
    /**
     * OVERRIDES the group `sourceType` implies — the capture service has
     * always allowed this server-side (blood bank and OT post through MANUAL
     * and state their real group), but the wire had no field for it, so a
     * caller could not reach it over HTTP. See `isIpWardPharmacyCode` for the
     * one caller that actually needs it today: an MTM-prefixed ward drug
     * line, captured as MANUAL (no dispensing module raised it) but shelved
     * under PHARMACY rather than MISCELLANEOUS.
     */
    chargeGroup: zod_1.z
        .enum([
        'ROOM_RENT',
        'NURSING',
        'DOCTOR_VISIT',
        'LABORATORY',
        'RADIOLOGY',
        'PROCEDURE',
        'OPERATION_THEATRE',
        'PHARMACY',
        'CONSUMABLE',
        'BLOOD_BANK',
        'PACKAGE',
        'MISCELLANEOUS',
    ])
        .optional(),
    /**
     * A NEWBORN'S TEST, CAPTURED ONTO THE MOTHER'S ADMISSION (desk, 2026-10-06).
     * The baby has no admission of their own, so the charge lands on the mother's
     * stay and these three say whose test it is — all three or none, and only
     * meaningful on a LAB charge. The ledger row keeps them so the laboratory
     * order made from it prints the baby, not the mother. See
     * `domain/lab-newborn.ts`.
     */
    ...lab_newborn_schema_1.newbornFieldsShape,
});
/**
 * Finalises ONE "Save All" batch: every LAB-sourceType charge captured under
 * this `captureBatchId` that is not yet bridged becomes ONE `LabOrder`
 * covering all of them, the same grouping `LabBillingBridgeService` gives an
 * OP invoice's lines. Called once, after the sequential captures that share
 * the id have all finished — never per line, or it would recreate the exact
 * one-order-per-test defect this exists to close.
 *
 * Safe to call with a batch id that named no LAB charge at all (an ordinary
 * room-rent/nursing Save All): `IpLabBridgeService.bridgeBatch` reads that as
 * nothing to do, not an error.
 */
exports.bridgeIpChargeBatchSchema = zod_1.z.object({
    captureBatchId: zod_1.z.string().uuid(),
});
exports.reverseIpChargeSchema = zod_1.z.object({
    /** Mandatory — a reversal with no reason is unauditable. */
    reason: zod_1.z.string().min(1).max(1000),
});
exports.listIpChargesQuerySchema = zod_1.z.object({
    status: exports.ipChargeLedgerStatusEnum.optional(),
    from: zod_1.z.coerce.date().optional(),
    to: zod_1.z.coerce.date().optional(),
});
// ---------------------------------------------------------------------------
// CHARGE DRAFTS — a line typed but not yet charged
// ---------------------------------------------------------------------------
/**
 * Desk, 2026-08-26: "need Draft button before Save… later they can edit delete
 * make changes. when they saved after only it should captured."
 *
 * A DRAFT IS AN INTENT, NOT MONEY, and its shape says so: no gross, no tax, no
 * payer split, no source type, no idempotency key. Everything a capture needs
 * beyond this is resolved at capture time from the masters as they stand THEN —
 * a draft that carried a price would be a quote going stale in a table.
 *
 * `unitRatePaise` is the single exception, and only for an OPEN-VALUE service
 * where the rate is the operator's own input rather than a resolved price. On
 * any other service the server ignores it, because a typed rate on a priced
 * service is exactly the override this billing model does not have.
 */
exports.ipChargeDraftInputSchema = zod_1.z.object({
    serviceId: zod_1.z.string().uuid(),
    /** Snapshotted so a draft LIST renders without joining the master. */
    serviceName: zod_1.z.string().min(1).max(255),
    serviceCode: zod_1.z.string().max(20).optional(),
    /**
     * NONNEGATIVE HERE, POSITIVE AT CAPTURE — and the difference is the point.
     *
     * Desk, 2026-08-28: "invalid request biody error under IP Billing screen".
     *
     * A staged row deliberately opens with NO quantity (desk, 2026-08-26: "make
     * qty blank, user should enter, don't show 1" — a pre-filled 1 is an answer
     * the operator never gave, on the column that decides what the line costs).
     * That blank is carried as 0. Requiring a positive number HERE therefore
     * rejected the very rows the product is designed to create: picking a service
     * and then saving the batch produced "Invalid request body" on every line
     * whose quantity had not been typed yet.
     *
     * A DRAFT IS INCOMPLETE BY DEFINITION — that is what distinguishes it from a
     * captured charge. So the draft store accepts a row nobody has finished, and
     * the rule that actually protects money stays where it belongs: the CAPTURE
     * schema still demands `positive()`, so a zero-quantity line cannot reach the
     * ledger, and `isDraftReady` on the screen still refuses to submit one.
     * Loosening this cannot bill anything; it only lets a half-typed line survive
     * being saved and reloaded, which is why drafts are stored server-side at all.
     */
    quantity: zod_1.z.number().int().nonnegative().max(10_000).default(1),
    /** C1-C4, or absent for "let the bed decide" — the default, and not the same statement as C1. */
    tier: zod_1.z.enum(['C1', 'C2', 'C3', 'C4']).optional(),
    /** Integer paise, OPEN-VALUE services only. */
    unitRatePaise: zod_1.z.number().int().nonnegative().max(100_000_000).optional(),
    remarks: zod_1.z.string().max(1000).optional(),
    otDoctors: zod_1.z.array(exports.ipOtDoctorSchema).max(30).optional(),
});
/**
 * SAVED IN A BATCH, because that is how the desk works: several lines are typed
 * and then kept in one act. One request also means one transaction — a partial
 * save that kept three of five lines would leave the operator to work out which
 * two to type again.
 */
exports.saveIpChargeDraftsSchema = zod_1.z.object({
    drafts: zod_1.z.array(exports.ipChargeDraftInputSchema).min(1).max(100),
    /**
     * Where the batch lands. DRAFT is a line still being typed; SAVED is one the
     * operator has decided to keep. Both are editable and both are deletable —
     * the difference is intent, and it is what the discharge gate reports.
     */
    stage: zod_1.z.enum(['DRAFT', 'SAVED']).default('DRAFT'),
});
/** Every field is optional on an update — the desk edits one cell at a time. */
exports.updateIpChargeDraftSchema = exports.ipChargeDraftInputSchema.partial();
/** One draft as the workstation reads it back. */
exports.ipChargeDraftResponseSchema = exports.ipChargeDraftInputSchema.extend({
    id: zod_1.z.string().uuid(),
    /**
     * DRAFT or SAVED — the two stages a line has before it is approved.
     *
     * APPROVED is deliberately not a value: an approved line is a row in
     * `ip_admission_charge_ledger` and no longer a draft at all. Carrying it here
     * as well would be the same charge in two places.
     */
    stage: zod_1.z.enum(['DRAFT', 'SAVED']),
    ipAdmissionId: zod_1.z.string().uuid(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
    createdByName: zod_1.z.string().nullable().optional(),
});
/** DRAFT → SAVED for a batch of lines the operator has decided to keep. */
exports.markIpChargeDraftsSavedSchema = zod_1.z.object({
    draftIds: zod_1.z.array(zod_1.z.string().uuid()).min(1).max(200),
});
//# sourceMappingURL=ip-charge.schema.js.map